import { h, Fragment } from "preact";
import { Card, Button, Space, Alert, Empty, Typography, Input } from 'antd';
import { useSettings } from '../hooks/useSettings';
import { SETTING_GROUPS } from '../constants/settingsConfig';
import ConsumerSelector from './ConsumerSelector';
import SettingsGroup from './SettingsGroup';
import ConfigurationSummary from './ConfigurationSummary';

const { Title, Text } = Typography;

const SettingsPage = () => {
    const {
        selectedConsumer,
        setSelectedConsumer,
        selectedPersonId,
        setSelectedPersonId,
        personIdInput,
        setPersonIdInput,
        settings,
        loading,
        hasChanges,
        loadConsumerSettings,
        handleSettingChange,
        handleToggle,
        resetToDefault,
        saveAllChanges,
        applyPersonId,
        clearPersonSelection
    } = useSettings();

    const handleConsumerSelect = (consumer) => {
        setSelectedConsumer(consumer);
        setSelectedPersonId(null);
        setPersonIdInput('');
        loadConsumerSettings(consumer.consumer_id);
    };

    const handlePersonIdChange = (e) => {
        setPersonIdInput(e.target.value);
    };

    const handleApplyPersonId = () => {
        applyPersonId();
    };

    const handleClearPerson = () => {
        clearPersonSelection();
    };

    const getTargetName = () => {
        if (selectedPersonId) {
            return `person ${selectedPersonId}`;
        }
        return selectedConsumer?.username || 'consumer';
    };

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <div>
                        <Title level={2} style={{ margin: 0 }}>Consumer Settings</Title>
                        <Text type="secondary">Configure security and verification settings for consumers and specific persons</Text>
                    </div>
                    <Space>
                        <Button icon="🔄"
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                onClick={resetToDefault}
                        >
                            Reset to Default
                        </Button>
                        <Button
                            type="primary"
                            icon="💾"
                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                            onClick={saveAllChanges}
                            loading={loading.saving}
                            disabled={!hasChanges || !selectedConsumer}
                        >
                            Save Changes
                        </Button>
                    </Space>
                </div>
            </div>

            {/* Consumer Selection */}
            <ConsumerSelector
                selectedConsumer={selectedConsumer}
                onConsumerSelect={handleConsumerSelect}
                hasChanges={hasChanges}
                loading={loading}
            />

            {/* Person ID Input */}
            {selectedConsumer && (
                <Card style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <Text style={{marginTop: '8px'}} strong>Person ID (Optional):</Text>
                        <Input
                            placeholder="Enter person ID for specific settings"
                            value={personIdInput}
                            onChange={handlePersonIdChange}
                            style={{ width: '250px' }}
                            disabled={loading.loadingSettings}
                            onPressEnter={handleApplyPersonId}
                        />
                        <Button
                            type="primary"
                            onClick={handleApplyPersonId}
                            disabled={loading.loadingSettings || !personIdInput.trim()}
                            loading={loading.loadingSettings}
                        >
                            Use Person ID
                        </Button>
                        {selectedPersonId && (
                            <Button
                                onClick={handleClearPerson}
                                disabled={loading.loadingSettings}
                            >
                                Clear Selection
                            </Button>
                        )}
                        <div style={{ flex: 1 }}>
                            <Text type="secondary">
                                {selectedPersonId
                                    ? `Currently configuring settings for person: ${selectedPersonId}`
                                    : `Currently configuring default settings for: ${selectedConsumer.username}`
                                }
                            </Text>
                        </div>
                    </div>
                </Card>
            )}

            {/* Empty State */}
            {!selectedConsumer && (
                <Card>
                    <Empty
                        description={
                            <div>
                                <Title level={4}>No Consumer Selected</Title>
                                <Text type="secondary">
                                    Please select a consumer from the dropdown above to configure their security settings.
                                </Text>
                            </div>
                        }
                    />
                </Card>
            )}

            {/* Unsaved Changes Warning */}
            {hasChanges && selectedConsumer && (
                <Alert
                    message={`You have unsaved changes for ${getTargetName()}`}
                    description="Don't forget to save your changes before switching consumers or leaving this page."
                    type="warning"
                    showIcon
                    closable
                    style={{ marginBottom: '24px' }}
                />
            )}

            {/* Settings Groups */}
            {selectedConsumer && (
                <>
                    {Object.values(SETTING_GROUPS).map(group => (
                        <SettingsGroup
                            key={group.title}
                            group={group}
                            settings={settings}
                            loading={loading}
                            onToggle={handleToggle}
                            onSettingChange={handleSettingChange}
                        />
                    ))}

                    {/* Configuration Summary */}
                    <ConfigurationSummary
                        settings={settings}
                        targetName={getTargetName()}
                    />

                    {/* Footer Actions */}
                    <Card>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <Button size="large">Cancel</Button>
                            <Button
                                type="primary"
                                size="large"
                                icon="💾"
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                onClick={saveAllChanges}
                                loading={loading.saving}
                                disabled={!hasChanges}
                            >
                                Save All Changes
                            </Button>
                        </div>
                    </Card>
                </>
            )}
        </div>
    );
};

export default SettingsPage;